<script setup lang="ts">
import { ref, useTemplateRef, computed, onMounted } from 'vue';
import { Button, Column, DataTable, Card, InputText, Menu, MultiSelect, Checkbox, InputNumber } from 'primevue';
import type { DataTableRowDoubleClickEvent, DataTableRowClickEvent, DataTableFilterEvent } from 'primevue';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import EditProductDialog from '@/products/components/EditProductDialog.vue';
import type { MenuItem } from 'primevue/menuitem';
import DeleteProductDialog from './DeleteProductDialog.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useProductAuditApi } from '@/audit/api/productAuditApi';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import type { ProductResponse } from '../api/common/productResponse';
import { useProductStore } from 'shared/products/stores/productStore';
import { useRefereshProductStore } from '../commands/refreshProductStore';
import { useRefereshProductGroupStore } from '../groups/commands/refreshProductGroupStore';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { ProductType } from 'shared/products/domain/productType';
import type { Product } from 'shared/products/domain/product';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore.ts';
import ImportProductsDialog from './ImportProductsDialog.vue';
import DeleteMultipleProductsDialog from './DeleteMultipleProductsDialog.vue';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useColumnSelector } from 'shared/common/composables/useColumnSelector';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { IconSharpClear } from '@iconify-prerendered/vue-ic';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const productStore = useProductStore();
const productGroupStore = useProductGroupStore();
const refreshProductStore = useRefereshProductStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const featureFlagStore = useFeatureFlagStore();

const productAuditApi = useProductAuditApi();

interface ProductItem extends Product {
  nameSortOrder: number;
  productGroupName: string | null;
  isSorted: boolean;
}

const allowedTypes = computed<ProductType[]>(() => {
  const types: ProductType[] = [];
  if (featureFlagStore.isLogYardEnabled()) types.push(ProductType.Log);
  if (featureFlagStore.isBulkEnabled()) types.push(ProductType.Bulk);
  if (featureFlagStore.isCoilEnabled()) types.push(ProductType.Coil);
  if (featureFlagStore.isPackagesEnabled()) types.push(ProductType.Dimension);
  return types;
});

const products = computed<ProductItem[]>(() => {
  const result = productStore.products
    .filter(p => allowedTypes.value.includes(p.type))
    .map(p => {
      const item: ProductItem = {
        id: p.id,
        name: p.name,
        description: p.description,
        externalCode: p.externalCode,
        quality: p.quality,
        color: p.color,
        nameSortOrder: 0,
        logData: p.logData,
        bulkData: p.bulkData,
        isSorted: !!p.logData,
        coilData: p.coilData,
        dimensionData: p.dimensionData,
        productGroupName: null,
        type: p.type,
        productGroupId: p.productGroupId
      };

      if (p.productGroupId) {
        const group = productGroupStore.getById(p.productGroupId);
        item.productGroupName = group?.name ?? null;
      }

      return item;
    });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (item) {
      item.nameSortOrder = i;
    }
  }

  return result;
});

function getFieldValue(data: unknown, field: string) {
  return field.split('.').reduce<any>((obj, key) => (obj == null ? obj : obj[key]), data);
}

// ---------------------------------------------------------------------------
// Columns: order, visibility, filters
// ---------------------------------------------------------------------------

type ColumnKey =
  | 'name'
  | 'type'
  | 'description'
  | 'externalCode'
  | 'quality'
  | 'productGroupName'
  | 'logDataMinDiameter'
  | 'logDataMaxDiameter'
  | 'logDataMaxLength'
  | 'logDataMinLength'
  | 'logDataAverageCylinderVolume'
  | 'logDataAveragePhysicalVolume'
  | 'isSorted'
  | 'species'
  | 'logSorterName'
  | 'sawLineName'
  | 'coilDiameter'
  | 'coilDepth'
  | 'bulkMaxWeight'
  | 'bulkMaxVolume';

type TableColumn = {
  field: string;
  key: ColumnKey;
  header?: string;
  dataType?: string;
  sortable?: boolean;
  filter?: boolean;
  filterField?: string;
  showFilterMatchModes?: boolean;
  showFilterOperator?: boolean;
};

const baseColumns: TableColumn[] = [
  { field: 'name', key: 'name', sortable: true, filter: true, filterField: 'name', showFilterMatchModes: true,
    showFilterOperator: true, },
  {
    field: 'type',
    key: 'type',
    sortable: true,
    filter: true,
    filterField: 'type',
    showFilterMatchModes: false,
    showFilterOperator: false,
  },
  { field: 'description', key: 'description', sortable: true, filter: true, filterField: 'description' },
  { field: 'externalCode', key: 'externalCode', sortable: true, filter: true, filterField: 'externalCode' },
  { field: 'quality', key: 'quality', sortable: true, filter: true, filterField: 'quality' },
  { field: 'productGroupName', key: 'productGroupName', sortable: true, filter: true, filterField: 'productGroupName' },
  {
    field: 'logData.minDiameter',
    key: 'logDataMinDiameter',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'logData.minDiameter',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'logData.maxDiameter',
    key: 'logDataMaxDiameter',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'logData.maxDiameter',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'logData.maxLength',
    key: 'logDataMaxLength',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'logData.maxLength',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'logData.minLength',
    key: 'logDataMinLength',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'logData.minLength',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'logData.averageCylinderVolume',
    key: 'logDataAverageCylinderVolume',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'logData.averageCylinderVolume',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'logData.averagePhysicalVolume',
    key: 'logDataAveragePhysicalVolume',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'logData.averagePhysicalVolume',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'isSorted',
    key: 'isSorted',
    sortable: true,
    filter: true,
    filterField: 'isSorted',
    showFilterMatchModes: false,
    showFilterOperator: false,
  },
  { field: 'logData.species', key: 'species', sortable: true, filter: true, filterField: 'logData.species' },
  { field: 'logData.logSorterName', key: 'logSorterName', sortable: true, filter: true, filterField: 'logData.logSorterName' },
  { field: 'logData.sawLineName', key: 'sawLineName', sortable: true, filter: true, filterField: 'logData.sawLineName' },
  {
    field: 'coilData.diameter',
    key: 'coilDiameter',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'coilData.diameter',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'coilData.depth',
    key: 'coilDepth',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'coilData.depth',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'bulkData.maxWeight',
    key: 'bulkMaxWeight',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'bulkData.maxWeight',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'bulkData.maxVolume',
    key: 'bulkMaxVolume',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'bulkData.maxVolume',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
];

const filters = ref();

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    type: { value: null, matchMode: FilterMatchMode.IN },
    description: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    externalCode: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    quality: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    productGroupName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    'logData.minDiameter': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'logData.maxDiameter': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'logData.maxLength': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'logData.minLength': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'logData.averageCylinderVolume': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'logData.averagePhysicalVolume': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    isSorted: { value: null, matchMode: FilterMatchMode.EQUALS },
    'logData.species': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'logData.logSorterName': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'logData.sawLineName': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'coilData.diameter': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'coilData.depth': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'bulkData.maxWeight': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'bulkData.maxVolume': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
  };
}

initFilters();

function clearFilter() {
  initFilters();
}

const columns = computed<(TableColumn & { header: string })[]>(() => {
  const t = translations.value;

  return baseColumns.map(col => {
    let header = '';

    switch (col.key) {
      case 'name': header = t.generic.name; break;
      case 'type': header = t.generic.type; break;
      case 'description': header = t.generic.description; break;
      case 'externalCode': header = t.generic.code; break;
      case 'quality': header = t.generic.quality; break;
      case 'productGroupName': header = t.generic.productGroup; break;
      case 'logDataMinDiameter': header = t.products.logData.minDiameter; break;
      case 'logDataMaxDiameter': header = t.products.logData.maxDiameter; break;
      case 'logDataMaxLength': header = t.products.logData.maxLength; break;
      case 'logDataMinLength': header = t.products.logData.minLength; break;
      case 'logDataAverageCylinderVolume': header = t.products.logData.averageCylinderVolume; break;
      case 'logDataAveragePhysicalVolume': header = t.products.logData.averagePhysicalVolume; break;
      case 'isSorted': header = t.products.logData.isSorted; break;
      case 'species': header = t.products.logData.species; break;
      case 'logSorterName': header = t.products.logData.logSorterName; break;
      case 'sawLineName': header = t.products.logData.sawLineName; break;
      case 'coilDiameter': header = t.products.coilData.diameter; break;
      case 'coilDepth': header = t.products.coilData.depth; break;
      case 'bulkMaxWeight': header = t.bulk.maxWeight; break;
      case 'bulkMaxVolume': header = t.bulk.maxVolume; break;
    }

    return { ...col, header };
  });
});

const defaultVisibleFields = ['name', 'type', 'description', 'externalCode', 'productGroupName'];

const {
  orderedColumns,
  selectedColumns: visibleColumns,
  setSelectedColumns,
  setOrder,
  reset,
} = useColumnSelector<TableColumn & { header: string }>({
  storageKey: 'product-table-columns',
  columns,
  defaultFields: defaultVisibleFields,
});

const typeFilterOptions = computed(() =>
  allowedTypes.value
    .map(value => ({ label: translations.value.productType(value), value }))
    .sort((a, b) => a.label.localeCompare(b.label))
);

const filteredCount = ref(0);

function onFilter(event: DataTableFilterEvent) {
  filteredCount.value = event.filteredValue?.length ?? products.value.length;
}


// ---------------------------------------------------------------------------
// Selection / row actions
// ---------------------------------------------------------------------------

const selectedProducts = ref<ProductItem[]>([]);
const loading = ref(false);
const menuProductId = ref<string>();

const productMenu = useTemplateRef('product-menu');
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const deleteMultipleDialog = useTemplateRef('delete-multiple-dialog');
const auditDialog = useTemplateRef('audit-dialog');
const importDialog = useTemplateRef('import-dialog');
const actionFailedAlert = useActionFailedAlert();

onMounted(() => {
  refreshProductStore.initialize();
  refreshProductGroupStore.initialize();
});

const productMenuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const productId = menuProductId.value;
      if (!productId) return;
      showProduct(productId);
    },
  },
  {
    label: translations.value.generic.copy,
    icon: 'pi pi-copy',
    command() {
      const productId = menuProductId.value;
      if (!productId) return;
      editDialog.value?.show({ copyProductId: productId });
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const productId = menuProductId.value;
      if (!productId) return;
      deleteDialog.value?.show(productId);
    }
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const productId = menuProductId.value;
      if (!productId) return;

      const response = await productAuditApi.execute({
        id: productId
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit
      });
    }
  }
]);

function onAddNewClick() {
  editDialog.value?.show({});
}

function onProductDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const product = ev.data as ProductResponse;
  showProduct(product.id);
}

function showProduct(productId: string) {
  editDialog.value?.show({ productId });
}

function onProductClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.product-name');
  if (!name) return;
  showProduct(ev.data.id);
}

function toggleProductMenu(ev: Event, productId: string) {
  menuProductId.value = productId;
  productMenu.value?.toggle(ev);
}

function onProductDeleted(productId: string) {
  selectedProducts.value = selectedProducts.value.filter(product => product.id !== productId);
}

function onEditLogyardProducts() {
  const productIds = selectedLogYardProducts.value.map(product => product.id);
  editDialog.value?.editMultipleProducts(productIds, ProductType.Log);
}

function onEditCoilsProducts() {
  const productIds = selectedCoilsProducts.value.map(product => product.id);
  editDialog.value?.editMultipleProducts(productIds, ProductType.Coil);
}

const selectedLogYardProducts = computed(() =>
  selectedProducts.value.filter(product => productStore.getById(product.id)?.type == ProductType.Log)
);

const selectedCoilsProducts = computed(() =>
  selectedProducts.value.filter(product => productStore.getById(product.id)?.type == ProductType.Coil)
);
</script>

<template>
  <Card class="product-card">
    <template #title>
      <div class="header-row">
        <span class="title-text">{{ translations.products.products }}</span>
        <span v-if="filteredCount < products.length" class="filtered-count">
          ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ products.length }})
        </span>

        <div class="header-actions">
          <Button v-if="filteredCount < products.length" type="button" severity="secondary"
             @click="clearFilter">
            <IconSharpClear />
            {{ translations.dataTable.clearFilters }}
          </Button>

          <Button v-if="selectedProducts.length > 1" severity="danger"
            :label="translations.products.actions.deleteMultipleProducts(selectedProducts.length)"
            @click="deleteMultipleDialog?.show(selectedProducts.map(product => product.id))">
          </Button>
          <Button v-if="selectedLogYardProducts.length > 1"
            :label="translations.products.actions.editLogYardProducts(selectedLogYardProducts.length)"
            @click="onEditLogyardProducts">
          </Button>
          <Button v-if="selectedCoilsProducts.length > 1"
            :label="translations.products.actions.editCoilProducts(selectedCoilsProducts.length)"
            @click="onEditCoilsProducts">
          </Button>
          <Button v-if="allowedTypes.length > 0" :label="translations.generic.add" @click="onAddNewClick">
          </Button>
          <Button v-if="allowedTypes.length > 0" :label="translations.generic.import" @click="importDialog?.show()">
          </Button>

          <InputText class="search-field" autofocus v-model="filters.global.value"
            :placeholder="translations.generic.search" />

        </div>
      </div>
    </template>
    <template #content>
      <DataTable class="product-table" sort-field="name" v-model:selection="selectedProducts" selectionMode="multiple"
        v-model:filters="filters" :value="products" data-key="id" scrollable scrollHeight="flex" meta-key-selection
        size="small" filterDisplay="menu" @filter="onFilter" paginator :rows="25" :rowsPerPageOptions="[25, 50, 100]"
        @row-dblclick="onProductDoubleClick" :loading="loading" @row-click="onProductClick" :reorderableColumns="true">
        <template #empty>{{ translations.products.noProductsFound }}</template>
        <template #loading>{{ translations.products.loadingProductData }}</template>

        <Column selectionMode="multiple" headerStyle="width: 3rem" column-key="multiple-select"></Column>

        <Column v-for="col in visibleColumns" :key="col.field" :field="col.field" :header="col.header"
          :sortable="col.sortable" :filter="col.filter" :filterField="col.filterField"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.dataType">

          <template #body="{ data }">
            <template v-if="col.key === 'name'">
              <span class="product-name">{{ data.name }}</span>
            </template>

            <template v-else-if="col.key === 'type'">
              {{ translations.productType(data.type) }}
            </template>

            <template v-else-if="col.key === 'isSorted'">
              {{ data.isSorted ? translations.generic.yes : translations.generic.no }}
            </template>

            <template v-else>
              {{ getFieldValue(data, col.field) }}
            </template>
          </template>

          <template v-if="col.filter" #filter="{ filterModel, filterCallback }">
            <template v-if="col.key === 'name'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.name.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="col.key === 'type'">
              <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="typeFilterOptions"
                optionLabel="label" optionValue="value" :placeholder="translations.generic.type" size="small"
                appendTo="self" style="min-width: 12rem" />
            </template>

            <template v-else-if="col.key === 'description'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.description.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="col.key === 'externalCode'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.code.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="col.key === 'quality'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.quality.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="col.key === 'productGroupName'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.productGroup.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="col.key === 'isSorted'">
              <label for="isSorted-filter" class="font-bold">{{ translations.products.logData.isSorted }}</label>
              <Checkbox v-model="filterModel.value" :indeterminate="filterModel.value === null" binary
                inputId="isSorted-filter" />
            </template>

            <template v-else-if="col.key === 'species'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.products.logData.species.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="col.key === 'logSorterName'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.products.logData.logSorterName.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="col.key === 'sawLineName'">
              <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.products.logData.sawLineName.toLocaleLowerCase()" fluid />
            </template>

            <template v-else-if="[
              'logDataMinDiameter', 'logDataMaxDiameter', 'logDataMaxLength', 'logDataMinLength',
              'logDataAverageCylinderVolume', 'logDataAveragePhysicalVolume',
              'coilDiameter', 'coilDepth', 'bulkMaxWeight', 'bulkMaxVolume'
            ].includes(col.key)">
              <InputNumber v-model="filterModel.value" mode="decimal" />
            </template>
          </template>
        </Column>

        <Column :header="translations.generic.color" column-key="color">
          <template #body="product">
            <div v-if="product.data.color" class="flex gap-2">
              <div class="w-6 h-6 rounded" :style="{ backgroundColor: `${product.data.color}` }">
              </div>
            </div>
          </template>
        </Column>

        <Column class="w-24 text-end!" column-key="options-btn">
          <template #header>
            <div class="flex justify-end w-full">
              <ColumnSelectorButton :columns="orderedColumns" :modelValue="visibleColumns"
                @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="reset" />
            </div>
          </template>
          <template #body="product">
            <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
              @click="(ev) => toggleProductMenu(ev, product.data.id)"></Button>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
  <EditProductDialog ref="edit-dialog"></EditProductDialog>
  <DeleteProductDialog ref="delete-dialog" @deleted="onProductDeleted"></DeleteProductDialog>
  <DeleteMultipleProductsDialog ref="delete-multiple-dialog" @deleted="onProductDeleted($event)"></DeleteMultipleProductsDialog>
  <ImportProductsDialog ref="import-dialog"></ImportProductsDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="product-menu" :model="productMenuItems" :popup="true" />
</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
}

.title-text {
  font-weight: 600;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  flex-wrap: wrap;
}

.search-field {
  max-width: 16em;
}

:deep(.product-name) {
  cursor: pointer;
}

:deep(.product-name:hover) {
  color: var(--p-primary-900);
  text-decoration: underline;
}

.product-card {
  overflow-y: hidden;
  height: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

:deep(.product-table) {
  flex: 1;
  min-height: 0;
}
</style>
